// SCR-PAY-001 — صفحه عمومی پرداخت لینک (US-BIL-033، 060): بدون ورود؛ نام مرکز، مبلغ و مهلت، وضعیت (باز، پرداخت‌شده، منقضی)، دکمه پرداخت ← انتقال به درگاه
// (IPaymentGateway.Initiate) و در بازگشت Verify سمت سرور (فقط Verify نهایی است — BR-BIL-035). درگاه نمونه همیشه با نشان «نمونه» (CLAUDE.md §10).
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, JalaliDate, LoadingState, Money, SampleBadge } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { bilKeys, SCR_PAY_001 } from './common';

export function PublicPayPage() {
  const { t } = useTranslation('bil');
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const api = usePublicApi();
  const queryClient = useQueryClient();
  const info = useQuery({
    queryKey: bilKeys.publicPayment(token),
    queryFn: () => unwrap(api.GET('/api/v1/billing/public/payments/{token}', { params: { path: { token } } })),
    retry: false,
  });
  const verify = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/billing/public/payments/{token}:verify', { params: { path: { token } } })),
    onSuccess: (view) => queryClient.setQueryData(bilKeys.publicPayment(token), view),
  });
  const pay = useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/billing/public/payments/{token}:pay', { params: { path: { token } } })),
    onSuccess: (result) => {
      window.location.assign(result.redirectUrl);
    },
  });
  // بازگشت از درگاه (هر پارامتر بازگشت) ← Verify سمت سرور یک‌بار.
  const verified = useRef(false);
  useEffect(() => {
    if (!verified.current && params.toString() !== '') {
      verified.current = true;
      verify.mutate();
    }
  }, [params, verify]);
  useEffect(() => {
    document.title = `${t('bil:pay.title')} · ${t('shell:app.staff_name')}`;
  }, [t]);

  const data = info.data;
  return (
    <Box data-screen-id={SCR_PAY_001} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 560, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <LocalHospitalOutlinedIcon color="primary" />
          <Typography sx={{ fontWeight: 600 }} data-testid="public-tenant-name">
            {data?.tenantName ?? t('shell:app.staff_name')}
          </Typography>
        </Stack>
        <Typography variant="h1" sx={{ mb: 2 }}>
          {t('bil:pay.title')}
        </Typography>
        {info.isPending ? <LoadingState rows={2} /> : null}
        {info.error ? <ErrorState error={info.error} /> : null}
        {data ? (
          <Stack spacing={2}>
            {data.isSample ? <SampleBadge /> : null}
            <Typography variant="h4" data-testid="pay-amount" data-amount={data.amount.amount}>
              <Money amountMinor={data.amount.amount} currency={data.amount.currency} />
            </Typography>
            <Typography color="text.secondary">
              {t(data.target === 'Deposit' ? 'bil:pay.deposit' : 'bil:pay.invoice')} · {t('bil:pay.expires')} <JalaliDate value={data.expiresAt} withTime />
            </Typography>
            {data.status === 'Paid' ? (
              <Alert severity="success" data-testid="pay-paid">
                {t('bil:pay.paid')} {data.reference ? <bdi dir="ltr">{data.reference}</bdi> : null}
              </Alert>
            ) : null}
            {data.status === 'Expired' || data.status === 'Cancelled' ? (
              <Alert severity="warning" data-testid="pay-closed">
                {t(`bil:pay.${data.status === 'Expired' ? 'expired' : 'cancelled'}`)}
              </Alert>
            ) : null}
            {data.status === 'Open' && data.lastAttempt === 'Unknown' ? <Alert severity="info">{t('bil:pay.unknown')}</Alert> : null}
            {data.status === 'Open' && data.lastAttempt === 'Failed' ? <Alert severity="error">{t('bil:pay.failed')}</Alert> : null}
            {data.status === 'Open' && !data.gatewayAvailable ? <Alert severity="warning">{t('bil:pay.unavailable')}</Alert> : null}
            {pay.error ? <ErrorState error={pay.error} compact /> : null}
            {verify.error ? <ErrorState error={verify.error} compact /> : null}
            {data.status === 'Open' ? (
              <Button variant="contained" size="large" onClick={() => pay.mutate()} disabled={!data.gatewayAvailable || pay.isPending} data-testid="pay-submit">
                {t('bil:pay.submit')}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
}
